import React from 'react';
import { NamedLink } from '../../components';
import {
  nearbyCities,
  stateGuideBlog,
  cityLabel,
  stateLabel,
} from '../../util/interlinking';

import css from './CityRelatedLinks.module.css';

/**
 * SEO internal-linking block for a city (SeoCategoryPage) page.
 *  - Breadcrumb: Home > [State] trailer rentals > this city (crawlable up-links)
 *  - "More trailer rentals in [State]": sibling city money pages
 *  - "[State] Trailer Towing Laws": the relevant guide blog
 *
 * Rendered server-side (data hydrated in loadData). Fully defensive: any
 * missing slice simply renders fewer links; never crashes / blanks the page.
 */
const CityRelatedLinks = props => {
  const { category, citySlug, stateSlugParam, categories = [], blogs = [], states = [] } = props;
  if (!category) return null;

  // The single-category API does NOT populate the `state` relation, but the
  // full categories list (hydrated in loadData) does. Resolve the current
  // city's state by finding it in that list; fall back to any state on the
  // category object itself.
  const norm = s => (typeof s === 'string' ? s.trim().toLowerCase() : '');
  const selfInList = Array.isArray(categories)
    ? categories.find(c => c && norm(c.slug) === norm(citySlug))
    : null;
  const stateObj = (selfInList && selfInList.state) || category.state || null;
  // Use the URL's state slug as the authoritative slug for building links (it
  // is where this page actually lives), so links can never disagree with the
  // current route and create a crawl trap. Fall back to the resolved object.
  const stateSlug =
    (stateSlugParam && String(stateSlugParam).trim()) ||
    (stateObj && stateObj.slug ? stateObj.slug : null);
  // Readable state name for labels: resolved object title, else derive from the
  // URL slug ("trailer-rentals-west-virginia" -> "West Virginia").
  const stateName =
    stateLabel(stateObj) ||
    (stateSlug
      ? String(stateSlug)
          .replace(/^trailer-rentals-/, '')
          .split('-')
          .map(w => w.charAt(0).toUpperCase() + w.slice(1))
          .join(' ')
      : '');

  // Sibling cities in the same state (exclude current).
  const siblings = stateSlug
    ? nearbyCities(stateObj, citySlug, categories, 8).filter(c => c && c.slug)
    : [];

  // Relevant guide (prefers "[State] towing laws").
  const guide = stateSlug ? stateGuideBlog(stateObj, blogs, states) : null;

  // Trailer-type rental guides (stable blog category hubs). These give the city
  // page contextual topical outbound links that match the per-type H2 sections
  // above, and cascade to every city hub without fragile per-slug mapping.
  // The heading + intro line vary per city (deterministic fnv1a on the slug) so
  // this block isn't verbatim-identical across every city page.
  const fnv1a = str => {
    let h = 0x811c9dc5;
    const s = String(str || '');
    for (let i = 0; i < s.length; i++) {
      h ^= s.charCodeAt(i);
      h = Math.imul(h, 0x01000193);
    }
    return h >>> 0;
  };
  const typeGuideHeadings = [
    'Trailer Type Rental Guides',
    'Rent by Trailer Type',
    'Browse Trailer Rentals by Type',
    'Trailer Rental Guides by Type',
  ];
  const typeGuideHeading =
    typeGuideHeadings[fnv1a(`${citySlug || stateSlug || 'x'}::tg`) % typeGuideHeadings.length];
  const typeGuides = [
    { label: 'Enclosed Cargo Trailer Rentals', category: 'enclosed-trailers' },
    { label: 'Dump Trailer Rentals', category: 'dump-trailers' },
    { label: 'Car Hauler Rentals', category: 'car-hauler-trailer' },
    { label: 'Flatbed Trailer Rentals', category: 'flatbed-trailers' },
    { label: 'Utility Trailer Rentals', category: 'utility-trailers' },
  ];

  // NOTE: the breadcrumb now renders at the TOP of the page (under the hero,
  // above the Quick Answer) in SeoCategoryPage.js. It was moved out of this
  // bottom block for better UX + an earlier crawlable up-link in the DOM.
  const hasSiblings = siblings.length > 0;
  const hasGuide = !!(guide && guide.slug);

  // The type-guide block always renders, so the section is never empty; the
  // early return only fires in the pathological no-data case.
  if (!hasSiblings && !hasGuide && !stateSlug) return null;

  return (
    <div className={css.cityLinks}>
      <div className={css.related} aria-label="Related trailer rental links">
        {hasSiblings ? (
          <div className={css.block}>
            <h2 className={css.blockTitle}>More trailer rentals in {stateName}</h2>
            <ul className={css.linkList}>
              <li>
                <NamedLink
                  name="StateListsPage"
                  params={{ state: stateSlug }}
                  className={css.primaryLink}
                >
                  All {stateName} trailer rentals
                </NamedLink>
              </li>
              {siblings.map(c => (
                <li key={c.slug}>
                  <NamedLink
                    name="SeoCategoryPage"
                    params={{ state: stateSlug, title: c.slug }}
                    className={css.link}
                  >
                    Trailer rentals in {cityLabel(c)}
                  </NamedLink>
                </li>
              ))}
            </ul>
          </div>
        ) : null}

        {hasGuide ? (
          <div className={css.block}>
            <h2 className={css.blockTitle}>Trailer Towing Guides</h2>
            <ul className={css.linkList}>
              <li>
                <NamedLink
                  name="SingleBlogPage"
                  params={{ title: guide.slug }}
                  className={css.link}
                >
                  {guide.title || `${stateName} trailer towing laws`}
                </NamedLink>
              </li>
            </ul>
          </div>
        ) : null}

        <div className={css.block}>
          <h2 className={css.blockTitle}>{typeGuideHeading}</h2>
          <ul className={css.linkList}>
            {typeGuides.map(g => (
              <li key={g.category}>
                <NamedLink
                  name="BlogPage"
                  to={{ search: `?category=${g.category}` }}
                  className={css.link}
                >
                  {g.label}
                </NamedLink>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
};

export default CityRelatedLinks;
